import {
  QueryClient,
  type DefaultError,
  type EnsureQueryDataOptions,
  type FetchQueryOptions,
  type InferDataFromTag,
  type QueryClientConfig,
  type QueryExecuteOptions,
  type QueryFilters,
  type QueryKey,
} from "@-/com.npmjs.tanstack__react-query";
import {
  InactiveQueryMemoryBudget,
  type InactiveQueryMemoryBudgetOptions,
} from "#inactive-query-memory-budget";

/** Keeps normal QueryClient semantics, including reads that don't emit cache events. */
export class MemoryBoundedQueryClient extends QueryClient {
  readonly inactiveMemoryBudget: InactiveQueryMemoryBudget;

  constructor(config?: QueryClientConfig, budget?: InactiveQueryMemoryBudgetOptions) {
    super(config);
    this.inactiveMemoryBudget = new InactiveQueryMemoryBudget(this, budget);
  }

  #touch(queryKey: QueryKey): void {
    const { queryHash } = this.defaultQueryOptions({ queryKey });
    this.inactiveMemoryBudget.touch(this.getQueryCache().get(queryHash));
  }

  #handoff<T>(queryHash: string, promise: Promise<T>): Promise<T> {
    const query = this.getQueryCache().get(queryHash);
    this.inactiveMemoryBudget.touch(query);
    // Keep the original return value/promise and error semantics. These callbacks return no data,
    // so they don't reintroduce the settled retryer's duplicate-result retention.
    void promise.then(
      () => this.inactiveMemoryBudget.touch(query),
      () => this.inactiveMemoryBudget.touch(query),
    );
    return promise;
  }

  override getQueryData<
    TQueryFnData = unknown,
    TTaggedQueryKey extends QueryKey = QueryKey,
    TInferredQueryFnData = InferDataFromTag<TQueryFnData, TTaggedQueryKey>,
  >(queryKey: TTaggedQueryKey): TInferredQueryFnData | undefined {
    this.#touch(queryKey);
    return super.getQueryData<TQueryFnData, TTaggedQueryKey, TInferredQueryFnData>(queryKey);
  }

  override getQueriesData<
    TQueryFnData = unknown,
    TQueryFilters extends QueryFilters = QueryFilters,
  >(filters: TQueryFilters): [QueryKey, TQueryFnData | undefined][] {
    for (const query of this.getQueryCache().findAll(filters)) {
      this.inactiveMemoryBudget.touch(query);
    }
    return super.getQueriesData<TQueryFnData, TQueryFilters>(filters);
  }

  override ensureQueryData<
    TQueryFnData,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
  >(options: EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>): Promise<TData> {
    return this.#handoff(
      this.defaultQueryOptions(options).queryHash,
      super.ensureQueryData(options),
    );
  }

  override fetchQuery<
    TQueryFnData,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = never,
  >(
    options: FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
  ): Promise<TData> {
    return this.#handoff(this.defaultQueryOptions(options).queryHash, super.fetchQuery(options));
  }

  override query<
    TQueryFnData,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = never,
  >(
    options: QueryExecuteOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey, TPageParam>,
  ): Promise<TData> {
    return this.#handoff(this.defaultQueryOptions(options).queryHash, super.query(options));
  }
}
